import {
  Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy, inject, signal, computed, SecurityContext,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { FikoytSayfalamaComponent } from '@/src/service/fikoyt-ui/src/lib/fikoyt-sayfalama/fikoyt-sayfalama.component';
import { PanoDurumService } from '@uygulamalar/hmi-pano/services/pano-durum.service';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { hmiAyarGetir } from '@uygulamalar/hmi-pano/config/hmi-parametre-setleri';
import { DomSanitizer } from '@angular/platform-browser';
import { IMailHesap, IMailMesaj } from '@/src/app/shared/interfaces/mail.interface';
import { normalizeMailKart } from '@/src/app/shared/utils/mail-ikart.utils';
import * as MailUI from '@/src/app/shared/utils/mail-ui.utils';

const DB_AGG = 'FikoYT.sistem.DB.hareketler.aggregate';

@Component({
  selector: 'hmi-posta',
  standalone: true,
  imports: [CommonModule, NzIconModule, NzSpinModule, NzEmptyModule, NzTagModule, NzButtonModule, NzBadgeModule, NzDividerModule, NzPopconfirmModule, NzTooltipModule, FikoytSayfalamaComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './posta.component.html',
  styleUrls: ['./posta.component.less'],
})
export class HmiPostaComponent implements OnInit, OnDestroy {
  private durum = inject(PanoDurumService);
  private req = inject(FikoYTRequestService);
  private fikoyt = inject(FikoYTService);
  private sanitizer = inject(DomSanitizer);
  private cd = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);

  // ─── Shared mail UI (template proxy) ─────────────────────────────
  gonderenIsim = MailUI.gonderenIsim;
  gonderenAdres = MailUI.gonderenAdres;
  avatarHarf = MailUI.avatarHarf;
  avatarRenk = MailUI.avatarRenk;
  mimeDecodeIsim = MailUI.mimeDecodeIsim;

  silmeYetkisi = signal(false);

  hesaplar = signal<IMailHesap[]>([]);
  seciliHesap = signal<IMailHesap | null>(null);
  mailler = signal<IMailMesaj[]>([]);
  yukleniyor = signal(false);
  seciliMail = signal<IMailMesaj | null>(null);
  hata = signal(false);
  aramaMetni = signal('');
  dahaFazlaVar = signal(false);

  sayfaBoyutu = 20;
  aktifSayfa = signal(1);
  toplamMail = signal(0);
  get MAIL_LIMIT() { return this.sayfaBoyutu; }

  aramaAcik = false;
  aramaAlanlari = { konu: true, gonderen: true, icerik: false, dosya: false };

  private trLower(s: string | undefined): string {
    return (s || '').toLocaleLowerCase('tr-TR');
  }

  filtrelenmisMailler = computed(() => {
    const metin = this.trLower(this.aramaMetni()).trim();
    if (!metin) return this.mailler();
    return this.mailler().filter(m =>
      (this.aramaAlanlari.konu && this.trLower(m.konu).includes(metin)) ||
      (this.aramaAlanlari.gonderen && this.trLower(m.kimden).includes(metin)) ||
      (this.aramaAlanlari.icerik && (this.trLower(m.icerik).includes(metin) || this.trLower(m.icerikHtml).includes(metin))) ||
      (this.aramaAlanlari.dosya && m.ekler?.some(e => this.trLower(e.dosyaAdi).includes(metin)))
    );
  });

  private yeniMailSub: any;

  private bekleyenMailId: string | null = null;

  ngOnInit() {
    this.yetkiKontrol();
    // Deep link: ?mailId=xxx ile direkt mail açma
    const qMailId = this.route.snapshot.queryParamMap.get('mailId');
    if (qMailId) this.bekleyenMailId = qMailId;
    this.hesaplariYukle();
    const io = this.fikoyt.io;
    if (io) {
      this.yeniMailSub = io.fromEvent('FikoYT.sistem.io.mail.yeni').subscribe((data: any) => {
        if (this.seciliHesap()) {
          if (data?.hesapId === this.seciliHesap()?._id) this.mailleriYukle();
        } else {
          // Hesap secim ekraninda — badge'leri guncelle
          this.hesaplariYukle();
        }
      });
    }
  }

  ngOnDestroy() {
    this.yeniMailSub?.unsubscribe();
  }

  geriDon() {
    if (this.seciliMail()) {
      this.seciliMail.set(null);
    } else if (this.seciliHesap()) {
      this.seciliHesap.set(null);
      this.mailler.set([]);
      this.seciliMail.set(null);
      this.aramaMetni.set('');
      this.aktifSayfa.set(1);
      this.toplamMail.set(0);
    } else {
      history.back();
    }
  }

  // ─── Yetki kontrolü ──────────────────────────────────────────────────────

  private async yetkiKontrol() {
    const kullanici = this.fikoyt.kimlik.aktifKullaniciJson();
    if (!kullanici) return;
    const yetkiler: string[] = kullanici.yetkiler || kullanici.veri?.yetkiler || [];
    if (yetkiler.includes('Süper Yönetici')) {
      this.silmeYetkisi.set(true);
      return;
    }
    // Firma yetki kaydını kontrol et
    const firmaId = hmiAyarGetir<string>('firmaId', '');
    if (!firmaId) return;
    try {
      const sonuc = await this.req.aggregate<any>(DB_AGG, [
        { $match: { vt: 'FikoYT.sistem.firma.yetkiler', firmaId, kullaniciId: kullanici._id } },
        { $limit: 1 },
      ]);
      const firmaYetkiler: string[] = sonuc?.[0]?.yetkiler || [];
      this.silmeYetkisi.set(firmaYetkiler.includes('mail.yonet'));
    } catch {}
  }

  // ─── Hesap yükleme (yetki bazli) ─────────────────────────────────────────

  async hesaplariYukle() {
    this.yukleniyor.set(true);
    this.hata.set(false);
    this.cd.detectChanges();
    const firmaId = hmiAyarGetir<string>('firmaId', '');
    const personel = this.durum.sonPersonel();
    const personelId = personel?._id || '';
    const departmanId = personel?.departmanId || '';
    const kullaniciId = personel?.kullaniciId || '';

    try {
      // Acik yetki kontrolu: sadece 'herkes' veya eski kayitlar (field yok)
      const yetkiOr: any[] = [
        { erisimModu: 'herkes' },
        { erisimModu: { $exists: false } },
      ];
      if (personelId) yetkiOr.push({ yetkiliPersoneller: personelId });
      if (departmanId) yetkiOr.push({ yetkiliDepartmanlar: departmanId });
      if (kullaniciId && kullaniciId !== personelId) {
        yetkiOr.push({ yetkiliPersoneller: kullaniciId });
      }
      // Gecici yetki kontrolu
      if (personelId) {
        yetkiOr.push({
          geciciYetkiler: {
            $elemMatch: {
              personelId: personelId,
              baslangic: { $lte: new Date().toISOString() },
              bitis: { $gte: new Date().toISOString() },
            },
          },
        });
      }

      const hesapMatch: any = {
        vt: 'FikoYT.sistem.mail.hesaplar',
        aktif: { $ne: false },
        silindi: { $ne: true },
        $or: yetkiOr,
      };
      if (firmaId) hesapMatch.firmaId = firmaId;

      const sonuc = await this.req.aggregate<IMailHesap>(DB_AGG, [
        { $match: hesapMatch },
        { $sort: { email: 1 } },
      ]);

      const hesapListe = sonuc || [];

      // Okunmamis sayilari
      if (hesapListe.length > 0) {
        const okunmamisMatch: any = {
          vt: 'FikoYT.sistem.mail.mesajlar',
          silindi: { $ne: true },
          'veri.okundu': { $ne: true },
          sahip: { $in: hesapListe.map(h => h._id) },
        };
        if (firmaId) okunmamisMatch['veri.firmaId'] = firmaId;
        try {
          const sayilar = await this.req.aggregate<any>(DB_AGG, [
            { $match: okunmamisMatch },
            { $group: { _id: '$sahip', sayi: { $sum: 1 } } },
          ]);
          const sayiMap = new Map((sayilar || []).map((s: any) => [s._id, s.sayi]));
          hesapListe.forEach(h => h.okunmamisSayisi = sayiMap.get(h._id) || 0);
        } catch {}
      }

      this.hesaplar.set(hesapListe);

      // Deep link: mailId varsa hesabını bul ve aç
      if (this.bekleyenMailId && hesapListe.length > 0) {
        this.deepLinkMailAc(this.bekleyenMailId, hesapListe);
        this.bekleyenMailId = null;
      } else if (hesapListe.length === 1 && !this.seciliHesap()) {
        this.hesapSec(hesapListe[0]);
      }
    } catch {
      this.hata.set(true);
    }
    this.yukleniyor.set(false);
    this.cd.detectChanges();
  }

  // ─── Hesap secimi ─────────────────────────────────────────────────────────

  private async deepLinkMailAc(mailId: string, hesapListe: IMailHesap[]) {
    try {
      const sonuc = await this.req.aggregate<any>(DB_AGG, [
        { $match: { _id: mailId, vt: 'FikoYT.sistem.mail.mesajlar' } },
        { $limit: 1 },
      ]);
      const mail = sonuc?.[0];
      if (!mail) return;
      const normalized = normalizeMailKart(mail);
      const hesap = hesapListe.find(h => h._id === mail.sahip) || hesapListe[0];
      if (hesap) {
        this.seciliHesap.set(hesap);
        this.mailleriYukle(1);
      }
      this.seciliMail.set(normalized);
      this.cd.detectChanges();
    } catch {}
  }

  hesapSec(hesap: IMailHesap) {
    this.seciliHesap.set(hesap);
    this.aramaMetni.set('');
    this.cd.detectChanges();
    this.mailleriYukle(1);
  }

  // ─── Mail yükleme ─────────────────────────────────────────────────────────

  async mailleriYukle(sayfa = 1) {
    const hesap = this.seciliHesap();
    if (!hesap) return;
    this.yukleniyor.set(true);
    this.hata.set(false);
    this.aktifSayfa.set(sayfa);
    this.cd.detectChanges();
    const firmaId = hmiAyarGetir<string>('firmaId', '');
    const match: any = { vt: 'FikoYT.sistem.mail.mesajlar', sahip: hesap._id, silindi: { $ne: true } };
    if (firmaId) match['veri.firmaId'] = firmaId;
    const skip = (sayfa - 1) * this.sayfaBoyutu;
    try {
      // Toplam sayıyı al
      const sayiSonuc = await this.req.aggregate<any>(DB_AGG, [
        { $match: match },
        { $count: 'toplam' },
      ]);
      this.toplamMail.set(sayiSonuc?.[0]?.toplam || 0);
      const sonuc = await this.req.aggregate<IMailMesaj>(DB_AGG, [
        { $match: match },
        { $sort: { tarih: -1 } },
        { $skip: skip },
        { $limit: this.sayfaBoyutu },
      ]);
      const liste = (sonuc || []).map(normalizeMailKart);
      this.mailler.set(liste);
      this.yanitDurumKontrol(liste);
    } catch {
      this.hata.set(true);
    }
    this.yukleniyor.set(false);
    this.cd.detectChanges();
  }

  /** Yanıtlanmış mail tespiti: inReplyTo referansı + konu bazlı (RE:/FW: prefix) */
  private yanitlananSet = new Set<string>();
  mailYanitlandiMi(mail: IMailMesaj): boolean {
    return this.yanitlananSet.has(mail._id || '');
  }
  private async yanitDurumKontrol(liste: IMailMesaj[]) {
    if (!liste.length) return;
    const hesap = this.seciliHesap();
    if (!hesap) return;
    const idSet = new Set<string>();

    // 1. inReplyTo bazlı: messageId'si başka mailin inReplyTo'sunda geçenler
    const msgIds = liste.map(m => m.messageId).filter(Boolean);
    if (msgIds.length) {
      try {
        const sonuc = await this.req.aggregate<any>(DB_AGG, [
          { $match: { vt: 'FikoYT.sistem.mail.mesajlar', 'veri.inReplyTo': { $in: msgIds } } },
          { $group: { _id: null, refs: { $addToSet: '$veri.inReplyTo' } } },
        ]);
        const flatRefs = new Set((sonuc?.[0]?.refs || []).flat());
        liste.forEach(m => { if (m.messageId && flatRefs.has(m.messageId)) idSet.add(m._id!); });
      } catch {}
    }

    // 2. Konu bazlı: "RE:" veya "FW:" ile başlayan mail varsa, orijinal konulu mail yanıtlanmış
    const konular = new Map<string, string[]>(); // normalize konu → [_id'ler]
    liste.forEach(m => {
      const konu = (m.konu || '').trim();
      const temiz = konu.replace(/^(RE|FW|FWD|YNT|İLT)\s*:\s*/i, '').trim().toLowerCase();
      if (!temiz) return;
      if (!konular.has(temiz)) konular.set(temiz, []);
      konular.get(temiz)!.push(m._id!);
    });
    // RE:/FW: ile başlayan konulardaki orijinal mailleri yanıtlanmış say
    liste.forEach(m => {
      const konu = (m.konu || '').trim();
      if (/^(RE|FW|FWD|YNT|İLT)\s*:/i.test(konu)) {
        const temiz = konu.replace(/^(RE|FW|FWD|YNT|İLT)\s*:\s*/i, '').trim().toLowerCase();
        const eslesen = konular.get(temiz);
        if (eslesen) {
          eslesen.forEach(id => { if (id !== m._id) idSet.add(id); });
        }
      }
    });

    this.yanitlananSet = idSet;
    this.cd.detectChanges();
  }

  sayfaDegisti(sayfa: number) {
    this.mailleriYukle(sayfa);
  }

  sayfaBoyutuDegisti(boyut: number) {
    this.sayfaBoyutu = boyut;
    this.mailleriYukle(1);
  }

  /** Sunucu taraflı regex arama */
  async sunucudaAra() {
    const metin = this.aramaMetni().trim();
    if (!metin) { await this.mailleriYukle(1); return; }
    const hesap = this.seciliHesap();
    if (!hesap) return;
    this.yukleniyor.set(true);
    this.hata.set(false);
    const firmaId = hmiAyarGetir<string>('firmaId', '');
    const match: any = { vt: 'FikoYT.sistem.mail.mesajlar', sahip: hesap._id, silindi: { $ne: true } };
    if (firmaId) match['veri.firmaId'] = firmaId;

    // Regex $or koşulları — Türkçe karakter uyumlu
    const regexOr: any[] = [];
    const trRegex = MailUI.turkceRegex(metin);
    const regexDeger = { $regex: trRegex, $options: 'i' };
    if (this.aramaAlanlari.konu) regexOr.push({ 'veri.konu': regexDeger });
    if (this.aramaAlanlari.gonderen) regexOr.push({ 'veri.kimden': regexDeger });
    if (this.aramaAlanlari.icerik) {
      regexOr.push({ 'icerikler.aciklama': regexDeger });
      regexOr.push({ 'icerikler.html': regexDeger });
    }
    if (this.aramaAlanlari.dosya) regexOr.push({ 'dosyalar.ismi': regexDeger });
    if (regexOr.length === 0) regexOr.push({ 'veri.konu': regexDeger });

    try {
      const sayiSonuc = await this.req.aggregate<any>(DB_AGG, [
        { $match: { ...match, $or: regexOr } },
        { $count: 'toplam' },
      ]);
      this.toplamMail.set(sayiSonuc?.[0]?.toplam || 0);

      const sonuc = await this.req.aggregate<IMailMesaj>(DB_AGG, [
        { $match: { ...match, $or: regexOr } },
        { $sort: { tarih: -1 } },
        { $limit: this.sayfaBoyutu },
      ]);
      this.mailler.set((sonuc || []).map(normalizeMailKart));
      this.aktifSayfa.set(1);
    } catch {
      this.hata.set(true);
    }
    this.yukleniyor.set(false);
    this.cd.detectChanges();
  }

  private _aramaTimer: any;

  aramaGirdisi(deger: string) {
    this.aramaMetni.set(deger);
    clearTimeout(this._aramaTimer);
    this._aramaTimer = setTimeout(() => this.sunucudaAra(), 500);
  }

  aramaAlaniToggle(alan: 'konu' | 'gonderen' | 'icerik' | 'dosya') {
    (this.aramaAlanlari as any)[alan] = !this.aramaAlanlari[alan];
    this.cd.detectChanges();
    if (this.aramaMetni()) {
      clearTimeout(this._aramaTimer);
      this._aramaTimer = setTimeout(() => this.sunucudaAra(), 300);
    }
  }

  aramayiTemizle() {
    this.aramaMetni.set('');
    this.mailleriYukle(1);
  }

  // ─── Mail secimi + okundu isaretleme ──────────────────────────────────────

  async mailSec(mail: IMailMesaj) {
    this.seciliMail.set(mail);
    const personelId = this.durum.sonPersonel()?._id;

    // Per-user okundu: okunduBy array'ine personel ekle
    if (personelId && mail._id) {
      const zatenOkundu = (mail as any).okunduBy?.includes(personelId);
      if (!zatenOkundu) {
        // Optimistic UI
        if (!(mail as any).okunduBy) (mail as any).okunduBy = [];
        (mail as any).okunduBy.push(personelId);
        mail.okundu = true; // backward compat

        // Badge guncelle
        const hesap = this.seciliHesap();
        if (hesap?.okunmamisSayisi) {
          hesap.okunmamisSayisi = Math.max(0, hesap.okunmamisSayisi - 1);
          this.hesaplar.update(h => [...h]);
        }

        try {
          await this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle',
            { _id: mail._id },
            { $addToSet: { 'veri.okunduBy': personelId }, $set: { 'veri.okundu': true } },
          );
        } catch {}
      }
    }

    await this.auditKaydet('okuma', { mesajId: mail._id, konu: mail.konu });
  }

  // ─── Per-user okundu kontrolu ─────────────────────────────────────────────

  mailOkunduMu(mail: IMailMesaj): boolean {
    const personelId = this.durum.sonPersonel()?._id;
    if ((mail as any).okunduBy?.length) {
      return (mail as any).okunduBy.includes(personelId);
    }
    return mail.okundu;
  }

  // ─── Mail silme ──────────────────────────────────────────────────────────

  async mailSil(mail: IMailMesaj) {
    if (!mail?._id) return;
    try {
      await this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle',
        { _id: mail._id },
        { $set: { silindi: true, silinmeTarihi: new Date().toISOString() } },
      );
      await this.auditKaydet('silme', { mesajId: mail._id, konu: mail.konu });
      this.seciliMail.set(null);
      this.mailleriYukle();
    } catch {}
  }

  // ─── Ek dosya indirme ─────────────────────────────────────────────────────

  ekIndir(ek: any) {
    this.auditKaydet('ek_indirme', { mesajId: this.seciliMail()?._id, dosyaAdi: ek.dosyaAdi });
    if (ek.url) {
      window.open(ek.url, '_blank');
    } else {
      const dosyaId = ek.dosyaId || ek._id;
      if (dosyaId && dosyaId !== 'undefined') window.open(MailUI.ekIndirUrl(dosyaId), '_blank');
    }
  }

  // ─── Audit trail ──────────────────────────────────────────────────────────

  private async auditKaydet(islem: string, detay: any) {
    const personel = this.durum.sonPersonel();
    try {
      await this.req.ekle('FikoYT.sistem.DB.hareketler.ekle', {
        vt: 'FikoYT.sistem.mail.audit',
        islem,
        personelId: personel?._id,
        personelIsim: `${personel?.ad || ''} ${personel?.soyad || ''}`.trim(),
        hesapId: this.seciliHesap()?._id,
        ...detay,
        tarih: new Date().toISOString(),
      });
    } catch {}
  }

  // ─── Yardimcilar ──────────────────────────────────────────────────────────

  snippet(mail: IMailMesaj): string {
    return MailUI.mailSnippet(mail.icerik, mail.icerikHtml);
  }

  guvenliHtml(html: string) {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

}
